/**
 * Thin promise wrapper over IndexedDB.
 *
 * Deliberately small: no schema opinions (those live in schema.js) and no
 * business logic. Everything above this file treats it as the disk.
 *
 * Storage can be genuinely unavailable — private browsing, a blocked origin, a
 * full quota. On the show floor that must surface as a visible failure rather
 * than a silently dropped supplier, so writes REJECT. Reads resolve to an empty
 * result instead, because a missing read is recoverable (we re-pull) while a
 * missing write loses a booth visit.
 *
 * ONE RULE ON TOP OF THAT: the database is partitioned per identity (see
 * schema.js), so `setIdentity` must run before any read or write. openDB is the
 * single chokepoint that enforces it — nothing below this file can reach the
 * wrong account's data, because there is no name to open until an identity has
 * been set.
 */

import { ALL_STORES, DB_VERSION, LEGACY_DB_NAME, META, STORE, dbNameFor, upgrades } from './schema';

/** Thrown when storage itself is unusable, so callers can tell it apart. */
export class StorageUnavailableError extends Error {
  constructor(cause) {
    super('Fair Tracker storage is unavailable on this device');
    this.name = 'StorageUnavailableError';
    this.cause = cause;
  }
}

let dbPromise = null;
let dbName = null;
let identity = null;

/**
 * The partition currently open, or null when signed out. sync.js reads this to
 * make sure a run that started under one account cannot finish under another.
 */
export function currentIdentity() {
  return identity;
}

/**
 * Point the data layer at one account's partition.
 *
 * Idempotent, and cheap when nothing changed — the common case is being called
 * on every render. A genuine change closes the open database first, so the
 * previous account's connection cannot outlive the switch.
 *
 * Pass null (or an identity with no userId) on sign-out: that leaves no name to
 * open, which is what makes a logged-out app structurally unable to read the
 * last buyer's captures.
 */
export function setIdentity(next) {
  const name = dbNameFor(next);
  if (name === dbName) return dbName;

  const previous = dbPromise;
  dbPromise = null;
  if (previous) {
    previous.then(
      (db) => {
        try {
          db.close();
        } catch (e) {
          /* already gone */
        }
      },
      () => {},
    );
  }

  dbName = name;
  identity = name ? { userId: next.userId, spaceId: next.spaceId || next.userId } : null;
  return dbName;
}

/** The raw open, against an explicit name. */
function openNamed(name) {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined' || !indexedDB) {
      reject(new StorageUnavailableError(new Error('no indexedDB')));
      return;
    }
    let req;
    try {
      req = indexedDB.open(name, DB_VERSION);
    } catch (e) {
      reject(new StorageUnavailableError(e));
      return;
    }

    req.onupgradeneeded = (event) => {
      const db = event.target.result;
      const from = event.oldVersion || 0;
      upgrades
        .filter((u) => u.version > from)
        .sort((a, b) => a.version - b.version)
        .forEach((u) => u.apply(db, req.transaction));
    };
    req.onsuccess = () => {
      const db = req.result;
      // If another tab upgrades the schema, close here so it isn't blocked.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => reject(new StorageUnavailableError(req.error));
    req.onblocked = () => reject(new StorageUnavailableError(new Error('blocked by another tab')));
  });
}

export function openDB() {
  if (dbPromise) return dbPromise;

  if (!dbName) {
    // Not a storage fault, but it reaches callers through the same channel: a
    // read degrades to empty and a write surfaces "could not save", which is
    // the right failure for a state that should be impossible.
    return Promise.reject(
      new StorageUnavailableError(new Error('no identity set — setIdentity() must run first')),
    );
  }

  const name = dbName;
  dbPromise = adoptLegacyOnce(name)
    .then(() => openNamed(name))
    .catch((e) => {
      // Don't cache the rejection — a later call may succeed (quota freed, a
      // blocking tab closed, etc.).
      if (dbName === name) dbPromise = null;
      throw e;
    });

  return dbPromise;
}

/* ── Adopting the pre-partition database ────────────────────────────────────
   Before partitioning there was one database for the whole origin. Whatever is
   in it belongs to SOMEBODY, and on a phone that somebody is almost certainly
   whoever signs in first — so the first partition to open takes it over, and it
   is deleted immediately after so no second account can inherit it.

   The alternative was discarding it, which on this app means throwing away a
   trip's worth of booth captures that never got the chance to sync. Adoption is
   the forgiving choice; the delete is what stops it becoming a permanent leak
   between accounts.

   Runs at most once per page load, and at most once per partition (the
   ADOPTED_LEGACY flag), and never blocks the open on failure — an account that
   cannot adopt still gets its own working, empty partition.
   ───────────────────────────────────────────────────────────────────────── */

let adoption = null;

function adoptLegacyOnce(name) {
  if (!adoption) adoption = adoptLegacy(name).catch(() => {});
  return adoption;
}

/**
 * Open the legacy database only if it already exists.
 *
 * `indexedDB.databases()` would answer this directly but is missing on Safari
 * and Firefox, so existence is detected the portable way: if onupgradeneeded
 * fires, this open CREATED it, which means there was nothing there. The husk is
 * deleted on the way out so the check stays cheap on later loads.
 */
function openLegacyIfExists() {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined' || !indexedDB) {
      resolve(null);
      return;
    }
    let existed = true;
    let req;
    try {
      req = indexedDB.open(LEGACY_DB_NAME, DB_VERSION);
    } catch (e) {
      resolve(null);
      return;
    }
    // Create nothing: a fresh database here is proof there was no legacy data.
    req.onupgradeneeded = () => {
      existed = false;
    };
    req.onsuccess = () => {
      const db = req.result;
      if (!existed || !db.objectStoreNames.contains(STORE.FAIRS)) {
        try {
          db.close();
        } catch (e) {
          /* ignore */
        }
        deleteDatabase(LEGACY_DB_NAME);
        resolve(null);
        return;
      }
      resolve(db);
    };
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
}

function readStore(db, store) {
  return new Promise((resolve) => {
    try {
      const req = db.transaction(store, 'readonly').objectStore(store).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    } catch (e) {
      resolve([]);
    }
  });
}

function writeStore(db, store, rows) {
  if (!rows || !rows.length) return Promise.resolve();
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(store, 'readwrite');
      const os = tx.objectStore(store);
      rows.forEach((row) => os.put(row));
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error || new Error('adoption aborted'));
      tx.onerror = () => reject(tx.error);
    } catch (e) {
      reject(e);
    }
  });
}

function readAdoptedFlag(db) {
  return new Promise((resolve) => {
    try {
      const store = db.transaction(STORE.META, 'readonly').objectStore(STORE.META);
      const req = store.get(META.ADOPTED_LEGACY);
      req.onsuccess = () => resolve(!!(req.result && req.result.v));
      req.onerror = () => resolve(false);
    } catch (e) {
      resolve(false);
    }
  });
}

async function adoptLegacy(name) {
  const legacy = await openLegacyIfExists();
  if (!legacy) return;

  const rows = {};
  try {
    const perStore = await Promise.all(ALL_STORES.map((store) => readStore(legacy, store)));
    ALL_STORES.forEach((store, i) => {
      rows[store] = perStore[i];
    });
  } finally {
    try {
      legacy.close();
    } catch (e) {
      /* ignore */
    }
  }

  // lastSyncAt is deliberately dropped. It is the one meta key that belongs to
  // a (space, owner) pair rather than to the buyer: carrying it over would make
  // the adopting account ask the server for changes "since" a clock it never
  // set, and everything older than that — which is all of its real data — would
  // never be pulled. A fresh partition has to start from zero.
  rows[STORE.META] = (rows[STORE.META] || []).filter((row) => row && row.k !== META.LAST_SYNC_AT);

  const total = ALL_STORES.reduce((n, store) => n + (rows[store] || []).length, 0);
  if (!total) {
    await deleteDatabase(LEGACY_DB_NAME);
    return;
  }

  const target = await openNamed(name);
  try {
    if (await readAdoptedFlag(target)) return;

    for (const store of ALL_STORES) {
      // Sequentially, not in parallel: one failing store must not leave the
      // others half-written behind a rejected Promise.all.
      // eslint-disable-next-line no-await-in-loop
      await writeStore(target, store, rows[store]);
    }
    await writeStore(target, STORE.META, [{ k: META.ADOPTED_LEGACY, v: Date.now() }]);
  } finally {
    try {
      target.close();
    } catch (e) {
      /* ignore */
    }
  }

  // Only now, with the data safely inside a partition, is it safe to drop — so
  // a failure anywhere above leaves the original intact for the next attempt.
  await deleteDatabase(LEGACY_DB_NAME);
}

/** Resolves either way: a delete that cannot proceed must not block a boot. */
export function deleteDatabase(name) {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    } catch (e) {
      resolve();
    }
  });
}

/** Run `fn(store)` in a transaction and resolve with its request result. */
function run(storeName, mode, fn) {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        let tx;
        try {
          tx = db.transaction(storeName, mode);
        } catch (e) {
          reject(new StorageUnavailableError(e));
          return;
        }
        const store = tx.objectStore(storeName);
        let result;
        try {
          const req = fn(store);
          if (req) req.onsuccess = () => { result = req.result; };
        } catch (e) {
          reject(e);
          return;
        }
        tx.oncomplete = () => resolve(result);
        tx.onabort = () => reject(tx.error || new Error('transaction aborted'));
        tx.onerror = () => reject(tx.error);
      }),
  );
}

/* ── Reads: degrade to an empty result ──────────────────────────────────── */

export function get(storeName, key) {
  return run(storeName, 'readonly', (s) => s.get(key)).catch(() => undefined);
}

export function all(storeName) {
  return run(storeName, 'readonly', (s) => s.getAll()).then(
    (r) => r || [],
    () => [],
  );
}

export function allByIndex(storeName, indexName, query) {
  return run(storeName, 'readonly', (s) => s.index(indexName).getAll(query)).then(
    (r) => r || [],
    () => [],
  );
}

export function count(storeName) {
  return run(storeName, 'readonly', (s) => s.count()).then(
    (r) => r || 0,
    () => 0,
  );
}

/* ── Writes: reject, so the caller can tell the user ────────────────────── */

export function put(storeName, value) {
  return run(storeName, 'readwrite', (s) => s.put(value));
}

/** One transaction for the whole batch — all of it lands, or none of it. */
export function putAll(storeName, values) {
  if (!values || !values.length) return Promise.resolve();
  return run(storeName, 'readwrite', (s) => {
    values.forEach((v) => s.put(v));
    return null;
  });
}

export function del(storeName, key) {
  return run(storeName, 'readwrite', (s) => s.delete(key));
}

export function delAll(storeName, keys) {
  if (!keys || !keys.length) return Promise.resolve();
  return run(storeName, 'readwrite', (s) => {
    keys.forEach((k) => s.delete(k));
    return null;
  });
}

export function clear(storeName) {
  return run(storeName, 'readwrite', (s) => s.clear());
}

/** `add` rather than `put`, so autoIncrement assigns the key. Resolves to it. */
export function add(storeName, value) {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const req = tx.objectStore(storeName).add(value);
        let key;
        req.onsuccess = () => { key = req.result; };
        tx.oncomplete = () => resolve(key);
        tx.onabort = () => reject(tx.error || new Error('transaction aborted'));
        tx.onerror = () => reject(tx.error);
      }),
  );
}

/** Best-effort quota reading for the storage warning (plan § 10). */
export async function estimateUsage() {
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const { usage, quota } = await navigator.storage.estimate();
      return { usage: usage || 0, quota: quota || 0, ratio: quota ? usage / quota : 0 };
    }
  } catch (e) {
    /* not supported — fall through */
  }
  return null;
}

/**
 * Ask the browser to make storage persistent. On iOS this is what stops site
 * data being evicted after seven days unused, which on a five-day trip would
 * mean losing the whole capture. Safe to call repeatedly.
 */
export async function requestPersistence() {
  try {
    if (navigator.storage && navigator.storage.persist) {
      if (await navigator.storage.persisted()) return true;
      return await navigator.storage.persist();
    }
  } catch (e) {
    /* not supported */
  }
  return false;
}

/** Test seam — drops the cached connection so a fresh open happens. */
export function _resetForTests() {
  dbPromise = null;
  dbName = null;
  identity = null;
  adoption = null;
}
